import type { Plugin } from '@nuxt/types';

import type { StoreConfig } from '~/modules/GraphQL/types';
import StoreConfigGql from '~/plugins/query/StoreConfig.gql';
import { useConfigStore } from '~/stores/config';

const fields = [
  'store_code', 'default_title', 'store_name', 'default_display_currency_code',
  'locale', 'header_logo_src', 'logo_width', 'logo_height', 'logo_alt',
] as const;
const queryContract = StoreConfigGql.replace(/\s+/g, ' ').trim();
const publicText = (value: unknown): string => (typeof value === 'string' ? value : '');

interface PublicConfigScope {
  store: string;
  locale: string;
  currency: string;
  site: string;
}

interface PublicLocaleDefaults {
  code?: string;
  storeCode?: string;
  defaultCurrency?: string;
}

function publicConfig(value: unknown): StoreConfig | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  if (!fields.every((field) => Object.prototype.hasOwnProperty.call(source, field))) return null;
  if (!['store_code', 'default_display_currency_code', 'locale'].every((field) => publicText(source[field]).length > 0)) return null;
  if (!['default_title', 'store_name', 'header_logo_src', 'logo_alt'].every((field) => source[field] === null || typeof source[field] === 'string')) return null;
  if (!['logo_width', 'logo_height'].every((field) => source[field] === null || typeof source[field] === 'string'
    || (typeof source[field] === 'number' && Number.isFinite(source[field])))) return null;
  return Object.fromEntries(fields.map((field) => [field, source[field]])) as StoreConfig;
}

function hydrationKey(scope: PublicConfigScope, config: StoreConfig): string | null {
  const store = scope.store || publicText(config.store_code);
  const currency = scope.currency || publicText(config.default_display_currency_code);
  if (!store || !currency || !scope.locale) return null;
  // This key is deliberately public. Never include auth, headers or API config.
  return JSON.stringify([queryContract, store, scope.locale, currency, scope.site]);
}

const storeConfigPlugin: Plugin = async ({ $pinia, app, $config }) => {
  const configStore = useConfigStore($pinia);
  const api = app.$vsf.$magento;
  const currentScope = (): PublicConfigScope => {
    const { state } = api.config;
    const site = publicText($config?.currentSite);
    // FE "cn" is not the backend's "en_GB" config locale. Follow the same
    // public locale defaults used by plugins/i18n before it normalizes cookies.
    const locale = publicText(app.i18n?.locale) || publicText(state.getLocale?.()) || site;
    const locales = (app.i18n?.locales || []) as Array<string | PublicLocaleDefaults>;
    const localeSettings = locales.find((entry): entry is PublicLocaleDefaults => typeof entry === 'object' && entry.code === locale);
    const settings = localeSettings || (app.i18n?.localeProperties as PublicLocaleDefaults | undefined);
    const currency = publicText(state.getLocale?.()) === locale ? publicText(state.getCurrency?.()) : '';
    return {
      store: publicText(state.getStore?.()) || publicText(settings?.storeCode),
      locale,
      currency: currency || publicText(settings?.defaultCurrency),
      site,
    };
  };
  const expected = currentScope();
  const hydrated = publicConfig(configStore.storeConfig);
  if (process.client && hydrated && configStore.storeConfigHydrationKey
    && (!expected.store || expected.store === hydrated.store_code)
    && configStore.storeConfigHydrationKey === hydrationKey(expected, hydrated)) {
    // Consume only the SSR handoff; this is not a settled/TTL client cache.
    configStore.storeConfigHydrationKey = null;
    return;
  }
  configStore.$patch((state) => {
    state.storeConfig = {};
    state.storeConfigHydrationKey = null;
  });
  let response;
  try {
    response = await api.api.customQuery({ query: StoreConfigGql });
  } catch {
    // These nine public presentation fields may fail soft. Keep the cleared
    // store/marker so the client still performs a fresh read; never change
    // private-cart or mutation transport error propagation in the proxy.
    return;
  }
  const data = response?.data as { storeConfig?: unknown } | undefined;
  const config = publicConfig(data?.storeConfig);
  if (response?.errors?.length || !config) return;
  const key = hydrationKey(expected, config);
  const current = currentScope();
  if (!key || key !== hydrationKey(current, config)
    || (current.store && current.store !== config.store_code)) return;
  configStore.$patch((state) => {
    state.storeConfig = config;
    state.storeConfigHydrationKey = process.client ? null : key;
  });
};

export default storeConfigPlugin;
